import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, CircleNotch, FunnelSimple, MagnifyingGlass, X,
  Receipt, ArrowUp, ArrowDown, CheckCircle, Clock, Warning,
} from "@phosphor-icons/react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PROVIDER_STYLES = {
  stripe:  { label: "Stripe",  cls: "bg-indigo-950 text-indigo-100 border-indigo-950" },
  paylink: { label: "Paylink", cls: "bg-emerald-800 text-emerald-50 border-emerald-800" },
};

const PURPOSE_LABELS = {
  sponsorship:      "Sponsored slot",
  speaker_featured: "Featured speaker",
};

const STATUS_STYLES = {
  initiated: { label: "Initiated", cls: "border-zinc-300 text-zinc-500", icon: Clock },
  paid:      { label: "Paid",      cls: "border-blue-600 text-blue-700 bg-blue-50", icon: CheckCircle },
  applied:   { label: "Applied",   cls: "border-blue-700 bg-blue-700 text-white", icon: CheckCircle },
  complete:  { label: "Complete",  cls: "border-zinc-950 bg-zinc-950 text-white", icon: CheckCircle },
  failed:    { label: "Failed",    cls: "border-red-500 text-red-600 bg-red-50", icon: Warning },
  unpaid:    { label: "Unpaid",    cls: "border-zinc-300 text-zinc-500", icon: Clock },
};

const PAGE_SIZE = 25;

export default function AdminPaymentsPage() {
  const { user, loading } = useAuth();
  const [filters, setFilters] = useState({ provider: "", purpose: "", status: "", q: "" });
  const [data, setData] = useState({ items: [], total: 0, totals_by_currency: {} });
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    setErr("");
    const params = { limit: PAGE_SIZE, offset };
    if (filters.provider) params.provider = filters.provider;
    if (filters.purpose)  params.purpose  = filters.purpose;
    if (filters.status)   params.status   = filters.status;
    if (filters.q)        params.q        = filters.q;
    api.get("/admin/payments", { params })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setBusy(false));
  }, [isAdmin, filters, offset]);

  // Reset pagination when filters change
  useEffect(() => { setOffset(0); }, [filters]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">Loading…</div>;
  if (!isAdmin) {
    return (
      <div data-testid="payments-noauth" className="px-6 md:px-10 lg:px-16 py-16 max-w-md">
        <div className="overline mb-3 text-red-600">Admin only</div>
        <h1 className="font-display font-black text-4xl mb-3">Not authorized</h1>
        <p className="text-zinc-600 mb-6">This page is available only to platform administrators.</p>
        <Link to="/" className="btn-secondary">Back to home</Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-payments-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/admin" className="overline text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1"><ArrowLeft size={11} /> Admin</Link>
        <span className="overline text-zinc-300">/</span>
        <span className="overline">Payments</span>
      </div>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-10 border-b border-zinc-950 pb-6">
        <div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-none">Payment ledger</h1>
          <p className="text-zinc-600 mt-3 max-w-xl">All checkouts across Stripe and Paylink — filter, search, and audit. Every transaction is stored regardless of whether the payment completed.</p>
        </div>
        <div className="flex gap-3" data-testid="payments-kpi-bar">
          {Object.entries(data.totals_by_currency).length === 0 && (
            <div className="border border-dashed border-zinc-300 px-5 py-4 text-xs font-mono text-zinc-400">No transactions match</div>
          )}
          {Object.entries(data.totals_by_currency).map(([cur, t]) => (
            <div key={cur} className="border-2 border-zinc-950 px-5 py-4 min-w-[160px]">
              <div className="overline text-zinc-500 mb-1">Gross · {cur.toUpperCase()}</div>
              <div className="font-display font-black text-3xl tracking-tight tabular leading-none">
                {cur.toUpperCase() === "USD" ? "$" : `${cur.toUpperCase()} `}{t.gross.toFixed(0)}
              </div>
              <div className="mt-2 text-[10px] font-mono text-zinc-500 uppercase tracking-wider flex gap-3">
                <span className="text-blue-700">{t.paid_count} paid</span>
                <span>{t.initiated_count} pending</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter bar */}
      <div className="grid md:grid-cols-12 gap-3 mb-6" data-testid="payments-filter-bar">
        <div className="md:col-span-4 flex items-center gap-2 border border-zinc-300 focus-within:border-zinc-950 px-3 h-11">
          <MagnifyingGlass size={14} className="text-zinc-400" />
          <input
            data-testid="filter-q"
            className="flex-1 bg-transparent outline-none text-sm py-2"
            placeholder="Search by conference, speaker, or session id…"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          {filters.q && (
            <button onClick={() => setFilters((f) => ({ ...f, q: "" }))} data-testid="clear-q" className="text-zinc-400 hover:text-zinc-950"><X size={14} /></button>
          )}
        </div>
        <FilterSelect testid="filter-provider" label="Provider" value={filters.provider} onChange={(v) => setFilters((f) => ({ ...f, provider: v }))} options={[["", "All providers"], ["stripe", "Stripe"], ["paylink", "Paylink"]]} />
        <FilterSelect testid="filter-purpose" label="Purpose" value={filters.purpose} onChange={(v) => setFilters((f) => ({ ...f, purpose: v }))} options={[["", "All purposes"], ["sponsorship", "Sponsored slot"], ["speaker_featured", "Featured speaker"]]} />
        <FilterSelect testid="filter-status" label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={[["", "All statuses"], ["initiated", "Initiated"], ["paid", "Paid"], ["applied", "Applied"], ["complete", "Complete"]]} />
        <div className="md:col-span-2 flex items-center gap-2 text-xs font-mono text-zinc-500 justify-end">
          {busy ? <><CircleNotch size={12} className="animate-spin" /> Loading</> : <>{data.total} transactions</>}
        </div>
      </div>

      {err && (
        <div data-testid="payments-error" className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 mb-6 text-sm">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="payments-table">
          <thead className="bg-[#FAFAFA] border-b border-zinc-200">
            <tr className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">
              <th className="text-start px-5 py-3">Date</th>
              <th className="text-start px-3 py-3">Provider</th>
              <th className="text-start px-3 py-3">Purpose</th>
              <th className="text-start px-3 py-3">Item</th>
              <th className="text-end px-3 py-3">Amount</th>
              <th className="text-start px-3 py-3">Status</th>
              <th className="text-start px-5 py-3">Session</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && !busy && (
              <tr><td colSpan={7} className="px-5 py-16 text-center text-zinc-400" data-testid="payments-empty">No transactions match these filters.</td></tr>
            )}
            {data.items.map((it, idx) => (
              <PaymentRow key={it.id || it.session_id} row={it} idx={offset + idx} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between" data-testid="payments-pagination">
          <div className="text-xs font-mono text-zinc-500">Page {currentPage} of {totalPages} · {data.total} total</div>
          <div className="flex gap-2">
            <button
              data-testid="page-prev"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="btn-secondary text-xs disabled:opacity-40"
              style={{ padding: ".45rem .9rem" }}
            >
              <ArrowUp size={12} className="-rotate-90" /> Prev
            </button>
            <button
              data-testid="page-next"
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="btn-secondary text-xs disabled:opacity-40"
              style={{ padding: ".45rem .9rem" }}
            >
              Next <ArrowDown size={12} className="-rotate-90" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="md:col-span-2">
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 border border-zinc-300 hover:border-zinc-950 focus:border-zinc-950 outline-none px-3 text-sm bg-white appearance-none"
        aria-label={label}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function PaymentRow({ row, idx }) {
  const prov = PROVIDER_STYLES[row.provider] || { label: row.provider || "?", cls: "bg-zinc-100 border-zinc-300 text-zinc-700" };
  const statusKey = row.applied ? "applied" : (row.payment_status || row.status);
  const stat = STATUS_STYLES[statusKey] || { label: statusKey || "unknown", cls: "border-zinc-300 text-zinc-500", icon: Clock };
  const StatIcon = stat.icon;
  const item = row.conference_title || row.speaker_name || "—";
  const amount = row.amount || 0;
  const cur = (row.currency || "usd").toUpperCase();
  const amountDisplay = cur === "USD" ? `$${amount.toFixed(2)}` : `${cur} ${amount.toFixed(2)}`;
  const shortSession = (row.session_id || "").slice(0, 12) + "…";
  return (
    <tr data-testid={`payment-row-${idx}`} className="border-b border-zinc-100 hover:bg-[#FAFAFA]">
      <td className="px-5 py-4 whitespace-nowrap text-xs font-mono text-zinc-600">
        {row.created_at ? format(new Date(row.created_at), "MMM d, yyyy") : "—"}
        <div className="text-[10px] text-zinc-400 mt-0.5">{row.created_at ? format(new Date(row.created_at), "HH:mm") : ""}</div>
      </td>
      <td className="px-3 py-4">
        <span className={`text-[9px] font-mono uppercase tracking-[0.15em] px-2 py-1 border ${prov.cls}`} data-testid={`row-provider-${idx}`}>
          {prov.label}
        </span>
      </td>
      <td className="px-3 py-4 text-xs font-mono uppercase tracking-wider text-zinc-500">{PURPOSE_LABELS[row.purpose] || row.purpose}</td>
      <td className="px-3 py-4">
        <div className="font-display font-medium text-sm line-clamp-1 max-w-[280px]">{item}</div>
        {row.tier && <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-0.5">Tier · {row.tier}</div>}
      </td>
      <td className="px-3 py-4 text-end font-mono text-sm tabular whitespace-nowrap">{amountDisplay}</td>
      <td className="px-3 py-4">
        <span className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.15em] px-2 py-1 border ${stat.cls}`} data-testid={`row-status-${idx}`}>
          <StatIcon size={10} weight={statusKey === "applied" ? "fill" : "regular"} /> {stat.label}
        </span>
      </td>
      <td className="px-5 py-4 font-mono text-[10px] text-zinc-400" title={row.session_id}>{shortSession}</td>
    </tr>
  );
}
